import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, Badge, Spinner } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input, FormField, Label } from '../../components/ui/Input';
import { useToast } from '../../contexts/ToastContext';
import { kycApi, type KycStatusResponse } from '../../api/kyc';
import { ApiError } from '../../api/client';
import { fmtDate } from '../../lib/format';

const DOC_TYPES = [
  { value: 'PASSPORT', label: 'Passport' },
  { value: 'NATIONAL_ID', label: 'National ID card' },
  { value: 'DRIVERS_LICENSE', label: "Driver's license" },
];

const COUNTRIES = [
  // Just a useful subset; user can also type a 2-letter code if needed
  { code: 'IN', name: 'India' },
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BR', name: 'Brazil' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'SG', name: 'Singapore' },
  { code: 'JP', name: 'Japan' },
  { code: 'KR', name: 'South Korea' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'NG', name: 'Nigeria' },
];

export default function KycSubmit() {
  const toast = useToast();
  const nav = useNavigate();

  const [statusData, setStatusData] = useState<KycStatusResponse | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(true);

  // form state
  const [fullName, setFullName] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [countryCode, setCountryCode] = useState('IN');
  const [documentType, setDocumentType] = useState('PASSPORT');
  const [documentNumber, setDocumentNumber] = useState('');
  const [idImage, setIdImage] = useState<File | null>(null);
  const [selfieImage, setSelfieImage] = useState<File | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    kycApi
      .status()
      .then(setStatusData)
      .catch(() => setStatusData({ status: 'NONE', submission: null }))
      .finally(() => setLoadingStatus(false));
  }, []);

  if (loadingStatus) {
    return (
      <div className="flex justify-center py-20">
        <Spinner size={40} />
      </div>
    );
  }

  const status = statusData?.status ?? 'NONE';

  // If already submitted or approved/rejected, show status card instead of form
  if (status === 'PENDING' || status === 'APPROVED') {
    return (
      <div className="max-w-2xl mx-auto py-10 px-4">
        <div className="mb-6">
          <Link
            to="/dashboard"
            className="text-violet-600 hover:underline text-sm font-semibold"
          >
            ← Back to dashboard
          </Link>
        </div>
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h1 className="font-display text-3xl font-bold">KYC verification</h1>
            <Badge kind={status === 'APPROVED' ? 'success' : 'warn'}>{status}</Badge>
          </div>

          {status === 'PENDING' && (
            <p className="text-ink-700">
              Your KYC submission is under review. We typically respond within 1–3 business
              days. You'll receive an email when there's a decision.
            </p>
          )}
          {status === 'APPROVED' && (
            <p className="text-success">
              ✓ Your KYC has been approved. Commission payouts can now be processed to your
              linked wallet.
            </p>
          )}

          {statusData?.submission && (
            <div className="mt-6 bg-paper-100 p-4 rounded-2xl space-y-2 text-sm">
              <div>
                <span className="text-ink-600">Submitted:</span>{' '}
                <span className="font-mono">{fmtDate(statusData.submission.submittedAt)}</span>
              </div>
              {statusData.submission.decidedAt && (
                <div>
                  <span className="text-ink-600">Decided:</span>{' '}
                  <span className="font-mono">{fmtDate(statusData.submission.decidedAt)}</span>
                </div>
              )}
              <div>
                <span className="text-ink-600">Document:</span>{' '}
                <span className="font-mono">{statusData.submission.documentType}</span>
              </div>
              <div>
                <span className="text-ink-600">Country:</span>{' '}
                <span className="font-mono">{statusData.submission.countryCode}</span>
              </div>
            </div>
          )}
        </Card>
      </div>
    );
  }

  // REJECTED users see prior decision + can resubmit
  const isResubmit = status === 'REJECTED';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!idImage || !selfieImage) {
      setError('Please attach both an ID image and a selfie');
      return;
    }
    if (idImage.size > 5 * 1024 * 1024 || selfieImage.size > 5 * 1024 * 1024) {
      setError('Each image must be under 5 MB');
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('fullName', fullName);
      fd.append('dateOfBirth', dateOfBirth);
      fd.append('countryCode', countryCode);
      fd.append('documentType', documentType);
      fd.append('documentNumber', documentNumber);
      fd.append('idImage', idImage);
      fd.append('selfieImage', selfieImage);

      await kycApi.submit(fd);
      toast.success('KYC submitted. You will hear back within 1–3 business days.');
      nav('/dashboard');
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Submission failed';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto py-10 px-4">
      <div className="mb-6">
        <Link
          to="/dashboard"
          className="text-violet-600 hover:underline text-sm font-semibold"
        >
          ← Back to dashboard
        </Link>
      </div>

      <div className="mb-8">
        <div className="eyebrow text-violet-600">
          {isResubmit ? 'KYC resubmission' : 'KYC verification'}
        </div>
        <h1 className="font-display text-4xl font-bold mt-2">
          {isResubmit ? 'Resubmit your details' : 'Verify your identity'}
        </h1>
        <p className="text-ink-700 mt-3">
          KYC is required before commission payouts. Your documents are encrypted at rest
          and only accessible to authorised compliance reviewers.
        </p>
      </div>

      {isResubmit && statusData?.submission?.adminNotes && (
        <Card className="mb-6 !border-danger/30 !bg-danger/5">
          <div className="eyebrow text-danger mb-2">Previous submission rejected</div>
          <p className="text-ink-800 text-sm">
            <span className="font-semibold">Reviewer note:</span>{' '}
            {statusData.submission.adminNotes}
          </p>
        </Card>
      )}

      <Card>
        <form onSubmit={handleSubmit} className="space-y-5">
          <FormField label="Full legal name" htmlFor="fullName">
            <Input
              id="fullName"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="As shown on your document"
              required
            />
          </FormField>

          <FormField label="Date of birth" htmlFor="dateOfBirth">
            <Input
              id="dateOfBirth"
              type="date"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              required
            />
          </FormField>

          <FormField label="Country" htmlFor="countryCode">
            <select
              id="countryCode"
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              required
              className="w-full px-4 py-3 rounded-2xl border border-paper-300 bg-white text-ink focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none transition"
            >
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name} ({c.code})
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="Document type" htmlFor="documentType">
            <select
              id="documentType"
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
              required
              className="w-full px-4 py-3 rounded-2xl border border-paper-300 bg-white text-ink focus:border-violet-500 focus:ring-2 focus:ring-violet-500/20 outline-none transition"
            >
              {DOC_TYPES.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
          </FormField>

          <FormField label="Document number" htmlFor="documentNumber">
            <Input
              id="documentNumber"
              value={documentNumber}
              onChange={(e) => setDocumentNumber(e.target.value)}
              placeholder="Number on your ID"
              required
            />
          </FormField>

          <div>
            <Label htmlFor="idImage">ID image (front)</Label>
            <input
              id="idImage"
              type="file"
              accept="image/jpeg,image/png"
              onChange={(e) => setIdImage(e.target.files?.[0] ?? null)}
              required
              className="block w-full text-sm text-ink-700 mt-1 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100"
            />
            <p className="text-xs text-ink-600 mt-1">
              JPEG or PNG, max 5 MB. Should be clear and readable.
            </p>
            {idImage && (
              <p className="text-xs text-success mt-1">
                ✓ {idImage.name} ({Math.round(idImage.size / 1024)} KB)
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="selfieImage">Selfie holding your ID</Label>
            <input
              id="selfieImage"
              type="file"
              accept="image/jpeg,image/png"
              onChange={(e) => setSelfieImage(e.target.files?.[0] ?? null)}
              required
              className="block w-full text-sm text-ink-700 mt-1 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100"
            />
            <p className="text-xs text-ink-600 mt-1">
              Your face and the ID must both be clearly visible.
            </p>
            {selfieImage && (
              <p className="text-xs text-success mt-1">
                ✓ {selfieImage.name} ({Math.round(selfieImage.size / 1024)} KB)
              </p>
            )}
          </div>

          {error && (
            <div className="p-3 rounded-2xl bg-danger/5 border border-danger/20 text-danger text-sm">
              {error}
            </div>
          )}

          <Button type="submit" isLoading={submitting} className="w-full">
            Submit for review
          </Button>
        </form>
      </Card>

      <p className="text-xs text-ink-600 mt-6 text-center">
        By submitting, you confirm the information is accurate and you consent to its
        processing for identity verification purposes.
      </p>
    </div>
  );
}
